import { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn, toStyleObject } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { createGeneralApi } from '../tbsGeneralApi'
import { resolveLayout } from '../ScreenGenerator'

export default function AccordionField({ id, elements, visible: fieldVisible, onEvent, onRenderFields, compoGroup, userData, class: cssClass, style: rawStyle }) {
  // `visible: false` (JSON, sur le champ AccordionField lui-même) → display:none posé directement
  // sur [data-tbs-root] : voir ChampField.jsx pour le détail (show()/hide() ciblent ce même élément).
  const visibilityStyle = fieldVisible === false ? { display: 'none' } : undefined
  const cssStyle = toStyleObject(rawStyle)
  const [open, setOpen]                 = useState(new Set([0]))
  const [hiddenSheets, setHiddenSheets] = useState(new Set())
  const [badgeOverrides, setBadgeOverrides] = useState({})
  const [labelOverrides, setLabelOverrides] = useState({})

  const openRef         = useRef(open)
  const compoGroupRef   = useRef(compoGroup ?? null)
  const userDataRef     = useRef(userData ?? null)
  const hiddenSheetsRef = useRef(new Set())
  const allRef          = useRef(elements ?? [])
  useLayoutEffect(() => { openRef.current = open; hiddenSheetsRef.current = hiddenSheets })

  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}
    const getVisible = hidden => allRef.current.filter(el => el.visible !== false && !hidden.has(el.id))
    window.tbsComponents[id] = {
      type: 'accordion',
      ...createGeneralApi(id, userDataRef.current),
      getOpen:       () => Array.from(openRef.current),
      setOpen:       indices => { const s = new Set(indices); openRef.current = s; setOpen(s) },
      getCompoGroup: () => compoGroupRef.current,
      setCompoGroup: v => { compoGroupRef.current = v },
      collapseAll: () => { const s = new Set(); openRef.current = s; setOpen(s) },
      expandAll:   () => {
        const s = new Set(getVisible(hiddenSheetsRef.current).map((_, i) => i))
        openRef.current = s
        setOpen(s)
      },
      collapse: sheetId => {
        const idx = getVisible(hiddenSheetsRef.current).findIndex(el => el.id === sheetId)
        if (idx < 0) return
        const s = new Set(openRef.current); s.delete(idx)
        openRef.current = s
        setOpen(s)
      },
      expand: sheetId => {
        const idx = getVisible(hiddenSheetsRef.current).findIndex(el => el.id === sheetId)
        if (idx < 0) return
        const s = new Set(openRef.current); s.add(idx)
        openRef.current = s
        setOpen(s)
      },
      // openRef/hiddenSheetsRef assignés de façon synchrone avant chaque setState : sans cela,
      // des appels chaînés (ex. showSheet() puis hideSheet()) liraient encore l'ancien état via
      // les refs (resynchronisées seulement au rendu suivant par le useLayoutEffect ci-dessus).
      showSheet:      sheetId => {
        const newHidden = new Set(hiddenSheetsRef.current); newHidden.delete(sheetId)
        const newVisible = getVisible(newHidden)
        const insertIdx = newVisible.findIndex(el => el.id === sheetId)
        hiddenSheetsRef.current = newHidden
        setHiddenSheets(newHidden)
        if (insertIdx >= 0) {
          const newOpen = new Set(); for (const i of openRef.current) newOpen.add(i < insertIdx ? i : i + 1)
          openRef.current = newOpen
          setOpen(newOpen)
        }
      },
      hideSheet:      sheetId => {
        const hidingIdx = getVisible(hiddenSheetsRef.current).findIndex(el => el.id === sheetId)
        const newHidden = new Set([...hiddenSheetsRef.current, sheetId])
        hiddenSheetsRef.current = newHidden
        setHiddenSheets(newHidden)
        if (hidingIdx >= 0) {
          const newOpen = new Set()
          for (const i of openRef.current) { if (i < hidingIdx) newOpen.add(i); else if (i > hidingIdx) newOpen.add(i - 1) }
          openRef.current = newOpen
          setOpen(newOpen)
        }
      },
      hideSheets:     exceptId => {
        const newHidden = new Set(allRef.current.map(el => el.id).filter(i => i !== exceptId))
        hiddenSheetsRef.current = newHidden
        setHiddenSheets(newHidden)
        openRef.current = new Set()
        setOpen(new Set())
      },
      setActiveSheet: sheetId => {
        const idx = getVisible(hiddenSheetsRef.current).findIndex(el => el.id === sheetId)
        if (idx >= 0) {
          const newOpen = new Set([...openRef.current, idx])
          openRef.current = newOpen
          setOpen(newOpen)
          onEvent(id, 'onClick', idx); onEvent(id, 'onActiveTab', idx)
        }
      },
      getListOfSheets: () => allRef.current.map(el => el.id),
    }
    // Chaque section (sheet) s'enregistre aussi sous son propre id — méthodes générales
    // uniquement (show/hide/addCss/...), ciblant son panneau via data-tbs-root={el.id}.
    // Toujours montée (ouverte ou non, visible ou non — même principe que les champs enfants),
    // donc enregistrée indépendamment de l'état d'ouverture/masquage.
    const sheets = allRef.current
    for (const el of sheets) {
      if (!el.id) continue
      window.tbsComponents[el.id] = {
        type: 'accordionsheet',
        ...createGeneralApi(el.id, el.userData),
        setBadge: text  => setBadgeOverrides(prev => ({ ...prev, [el.id]: text })),
        setLabel: label => setLabelOverrides(prev => ({ ...prev, [el.id]: label })),
      }
    }
    return () => {
      delete window.tbsComponents[id]
      for (const el of sheets) { if (el.id) delete window.tbsComponents[el.id] }
    }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // openRef assigné de façon synchrone avant setOpen (jamais depuis l'intérieur de son updater,
  // non garanti synchrone) : sans cela, des clics rapprochés font différer/réordonner onActiveTab
  // par rapport à onClick (l'updater de plusieurs setOpen() en rafale peut être batché par React
  // et exécuté hors ordre relatif aux appels onEvent qu'il contiendrait).
  const toggle = useCallback(i => {
    onEvent(id, 'onClick', i)
    const next = new Set(openRef.current)
    next.has(i) ? next.delete(i) : next.add(i)
    openRef.current = next
    setOpen(next)
    onEvent(id, 'onActiveTab', i)
  }, [id, onEvent])

  const all     = elements ?? []
  const visible = all.filter(el => el.visible !== false && !hiddenSheets.has(el.id))
  const hidden  = all.filter(el => el.visible === false  ||  hiddenSheets.has(el.id))

  return (
    <>
      <div id={id} data-tbs-root={id} className={cn('flex flex-col border border-[hsl(var(--border))] rounded-md overflow-hidden mb-2', cssClass)} style={{ ...cssStyle, ...visibilityStyle }}>
        {visible.map((el, i) => {
          const isOpen = open.has(i)
          return (
            <div key={el.id ?? i} className="border-b border-[hsl(var(--border))] last:border-b-0">
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => toggle(i)}
                data-tbs-accordion-trigger-id={el.id ?? i}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold bg-[hsl(var(--muted))] hover:bg-[hsl(var(--accent))] transition-colors text-left"
              >
                <span className="flex items-center gap-2">
                  {el.icon && <i className={`${el.icon} text-[hsl(var(--muted-foreground))]`} aria-hidden="true" />}
                  {labelOverrides[el.id] ?? el.title}
                  <Badge value={badgeOverrides[el.id] ?? el.badge} />
                </span>
                <ChevronDown
                  size={15}
                  className={cn('shrink-0 text-[hsl(var(--muted-foreground))] transition-transform duration-200', isOpen && 'rotate-180')}
                />
              </button>
              <div id={el.id} hidden={!isOpen} data-tbs-accordion-panel-id={el.id ?? i} data-tbs-root={el.id} className={cn('px-4 pt-4 pb-1 bg-[hsl(var(--background))]', el.class)} style={toStyleObject(el.style)}>
                {onRenderFields(el.fields, resolveLayout(el))}
              </div>
            </div>
          )
        })}
      </div>
      {hidden.map(el => (
        <div key={el.id} id={el.id} data-tbs-root={el.id} className={el.class || undefined} style={{ ...toStyleObject(el.style), display: 'none' }}>
          {onRenderFields(el.fields, resolveLayout(el))}
        </div>
      ))}
    </>
  )
}
